import React, { memo, useRef } from 'react';
import { styled, css } from 'styled-components';
import { GridChannel } from './GridChannel';
import { GridPlaceholder } from './GridPlaceholder';
import { GridPositionLine } from './GridPositionLine';
import { GridScrollButtons } from './GridScrollButtons';
import { GridTimeMarkers } from './GridTimeMarkers';
import { useScrollToInitialPosition } from './use-scroll-to-initial-position';
import { Colors } from '../../../../../play-core/styles/Colors';
import { hideScrollbar, smallScrollbar } from '../../../../../play-core/styles/style-helper';
import { zIndex } from '../../../../../play-core/styles/z-index';
import { calculateStickyPosition } from '../tv-guide-helper';
import { gridScrollbarHeight } from '../tv-guide-types';
import { useNowMarkerPosition } from '../use-now-marker-position';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const TvGuideGrid = /*#__PURE__*/memo(({
  time,
  date,
  programs,
  activeChannelId,
  pixelsPerMinute,
  placeholderChannelCount,
  timelineIntervalInMin,
  isLoading,
  onSelectProgram,
  analyticsPageId,
  stickyTopPosition
}) => {
  const now = useNowMarkerPosition(date, pixelsPerMinute);
  const timelineScrollContainerRef = useRef(null);
  const guideScrollContainerRef = useRef(null);
  useScrollToInitialPosition(timelineScrollContainerRef, time, pixelsPerMinute);
  const onScroll = event => {
    if (guideScrollContainerRef.current && timelineScrollContainerRef.current && guideScrollContainerRef.current.scrollLeft !== timelineScrollContainerRef.current.scrollLeft) {
      switch (event.target) {
        case timelineScrollContainerRef.current:
          guideScrollContainerRef.current.scrollLeft = timelineScrollContainerRef.current.scrollLeft;
          break;
        case guideScrollContainerRef.current:
          timelineScrollContainerRef.current.scrollLeft = guideScrollContainerRef.current.scrollLeft;
          break;
        default:
      }
    }
  };
  return /*#__PURE__*/_jsxs(Layout, {
    children: [/*#__PURE__*/_jsx(TimeScroller, {
      stickyTopPosition: stickyTopPosition,
      children: /*#__PURE__*/_jsx(TimelineScrollContainer, {
        onScroll: onScroll,
        ref: timelineScrollContainerRef,
        children: /*#__PURE__*/_jsx(GridTimeMarkers, {
          pixelsPerMinute: pixelsPerMinute,
          timelineIntervalInMin: timelineIntervalInMin,
          programs: programs,
          now: now
        })
      })
    }), /*#__PURE__*/_jsxs(ScrollContainer, {
      ref: guideScrollContainerRef,
      onScroll: onScroll,
      children: [/*#__PURE__*/_jsx(GridPositionLine, {
        pixelsPerMinute: pixelsPerMinute,
        programs: programs,
        now: now
      }), programs?.map(programGuide => /*#__PURE__*/_jsx(GridChannel, {
        pixelsPerMinute: pixelsPerMinute,
        programGuide: programGuide,
        activeChannelId: activeChannelId,
        date: date,
        now: now,
        onSelectProgram: onSelectProgram,
        scrollRef: timelineScrollContainerRef,
        analyticsPageId: analyticsPageId
      }, programGuide.channel.urn)), /*#__PURE__*/_jsx(GridPlaceholder, {
        isLoading: isLoading,
        pixelsPerMinute: pixelsPerMinute,
        placeholderChannelCount: placeholderChannelCount
      })]
    }), /*#__PURE__*/_jsx(OverlayContainer, {
      children: /*#__PURE__*/_jsx(GridScrollButtons, {
        scrollRef: timelineScrollContainerRef,
        date: date,
        pixelsPerMinute: pixelsPerMinute,
        stickyTopPosition: stickyTopPosition
      })
    })]
  });
});
export const Layout = styled.div.withConfig({
  displayName: "TvGuideGrid__Layout",
  componentId: "sc-73yukf-0"
})(["position:relative;margin-top:5px;"]);
const scrollContainerStyles = css(["display:grid;overflow-y:auto;-webkit-overflow-scrolling:touch;"]);
export const ScrollContainer = styled.div.withConfig({
  displayName: "TvGuideGrid__ScrollContainer",
  componentId: "sc-73yukf-1"
})(["", " ", ";&::-webkit-scrollbar{height:", "px;}&::-webkit-scrollbar-thumb{border:0;border-top:3px solid transparent;border-bottom:3px solid transparent;}"], scrollContainerStyles, smallScrollbar(), gridScrollbarHeight);
const OverlayContainer = styled.div.withConfig({
  displayName: "TvGuideGrid__OverlayContainer",
  componentId: "sc-73yukf-2"
})(["position:absolute;top:0;left:0;height:100%;width:100%;pointer-events:none;"]);
const TimeScroller = styled.div.withConfig({
  displayName: "TvGuideGrid__TimeScroller",
  componentId: "sc-73yukf-3"
})(["display:grid;height:48px;z-index:", ";", ""], zIndex.floatingAction, ({
  stickyTopPosition
}) => calculateStickyPosition(stickyTopPosition));
const TimelineScrollContainer = styled.div.withConfig({
  displayName: "TvGuideGrid__TimelineScrollContainer",
  componentId: "sc-73yukf-4"
})(["", ";", ";background-color:", ";"], scrollContainerStyles, hideScrollbar(), Colors.GREY_1a1a1a);